/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Heading from '../../../../components/typography';
import { ProfileContainer, ProfileContentWrapper } from '../profile.styled';
import Button from '../../../../components/button';
import {
  CustomerCompanyService,
  VendorCompanyService,
  type Wealthlane_CustomerService_Dtos_VendorCompany_CustomerCompayDetailDto,
} from '../../../../services/wealthlane-customer-services';
import moment from 'moment';
import CompanyModal from './companyModal';
import AnimationWrapper from '../../../../components/animation/animationWrapper';
import { SkeletonCircle, SkeletonLine } from '../../../../components/skeleton';
import { handleOpenFile, taxFormatWithDash, toCamelCase } from '../../../../utils/global';
import { useBoolean, useWealthlaneFinanceIntegrationApi } from '../../../../hooks';
import { InputField } from '../../../participants/participant.styled';
import Spinners from '../../../../components/spinner';
import { PrivateTickerStockImportService } from '../../../../services/wealthlane-finance-integration-services';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import ToastComponent from '../../../../components/toast';
import { TransferAgentIntegrationService } from '../../../../services/wealthlane-award-services';
import { useQueryClient } from 'react-query';
import { FormWrapper } from '../../../../components/input-text/input.styled';

export interface ProfileComponentType {
  data?: Wealthlane_CustomerService_Dtos_VendorCompany_CustomerCompayDetailDto;
  loading: boolean;
}

export const getProfileListData = (
  fieldData: string | string[] | null | undefined
): JSX.Element => (
  <>
    <Heading
      title={(fieldData as unknown as boolean) || 'Not Specified'}
      variant='body-s'
      className={(fieldData as unknown as boolean) ? '' : 'profile-text-gray'}
    />
  </>
);

const CompanyComponent = ({ data, loading }: ProfileComponentType): JSX.Element => {
  const { handleRequest } = useWealthlaneFinanceIntegrationApi();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [importLoader, setImportLoader] = useState<boolean>(false);
  const [editStatus, setEditStatus] = useState<boolean>(false);
  const [dtcParticipantId, setDtcParticipantId] = useState<string>('');
  const [dtcLoading, setDtcLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data?.dtcParticipantId) {
      setDtcParticipantId(data.dtcParticipantId);
    }
  }, [data]);

  const downloadTemplate = (name: string): void => {
    handleRequest(
      PrivateTickerStockImportService.postApiFinanceIntegrationApiAppPrivateTickerStockImportDownloadSample()
    )
      .then((data: any) => {
        if (data != null) {
          handleOpenFile(`${name}.xlsx`, data.content);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
      });
  };
  const importExcel = (event: any): void => {
    if (!isEmpty(event.target.files)) {
      const payload = {
        File: event.target.files[0],
      };
      setImportLoader(true);
      handleRequest(
        PrivateTickerStockImportService.postApiFinanceIntegrationApiAppPrivateTickerStockImportImport(
          payload
        )
      )
        .then((data: any) => {
          if (data) {
            toast.success(
              <ToastComponent label='Success' message='Private ticker import job submitted.' />
            );
            setImportLoader(false);
          }
        })
        .catch((error: any) => {
          console.log('error', error);
          setImportLoader(false);
        })
        .finally(() => {
          setImportLoader(false);
        });
      event.target.value = null;
    }
  };

  const handleSave = () => {
    setDtcLoading(true);
    handleRequest(
      CustomerCompanyService.putApiCustomerApiAppVendorCompanyDtcParticipantId({
        dtcParticipantId: dtcParticipantId,
      })
    )
      .then((res) => {
        if (res.success) {
          void queryClient.invalidateQueries('companyProfileDetails');
          toast.success(<ToastComponent label='Success' message='DTC Participant ID edited.' />);
          setEditStatus(false);
          setDtcLoading(false);
        }
      })
      .catch((err) => {
        console.log('err', err);
        setDtcLoading(false);
        setDtcParticipantId('');
      })
      .finally(() => {
        setEditStatus(false);
        setDtcLoading(false);
      });
  };

  const COMPANY_DETAILS = [
    {
      title: 'Company Name',
      icon: 'apartment',
      isContent: false,
      content: getProfileListData(data?.companyName),
    },
    {
      title: 'Tax ID',
      icon: 'pin',
      isContent: false,
      content: getProfileListData(
        data?.taxId != null ? taxFormatWithDash(String(data?.taxId)) : ''
      ),
    },
    {
      title: 'Ticker',
      icon: 'confirmation_number',
      isContent: false,
      content: getProfileListData(data?.ticker),
    },
    {
      title: 'Primary Stock Exchange',
      icon: 'monitoring',
      isContent: false,
      content: (
        <div className='flex items-center justify-between w-full'>
          {getProfileListData(data?.primaryStockExchange)}
          {data?.primaryStockExchange === 'Private' && (
            <div>
              <InputField>
                <Button
                  variant={'secondary-white'}
                  title={'Download Template'}
                  icon={'cloud_upload'}
                  onClick={() => {
                    downloadTemplate('Private Stock Data Sample');
                  }}
                />

                <div className='input-file-container'>
                  <input
                    className='input-file'
                    id='my-file'
                    type='file'
                    accept='.xlsx, .xls'
                    onChange={(e: any) => {
                      importExcel(e);
                      // fileImport(e);
                    }}
                  />
                  {!importLoader ? (
                    <label
                      className='text-center dt-btn dt-btn-outline-primary dt-btn-sm input-file-trigger sm:text-left'
                      htmlFor='my-file'>
                      Import Bulk XLS
                    </label>
                  ) : (
                    <label
                      className='flex items-center gap-2 text-center dt-btn dt-btn-outline-primary dt-btn-sm input-file-trigger sm:text-left'
                      htmlFor='my-file'>
                      <Spinners className='spinner-container' /> Import Bulk XLS
                    </label>
                  )}
                </div>
              </InputField>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Month/Year of Incorporation (Optional)',
      icon: 'event',
      isContent: false,
      content: getProfileListData(
        (data?.dateOfIncorporation as unknown as boolean)
          ? moment(data?.dateOfIncorporation).format('MMMM, YYYY')
          : ''
      ),
    },
    {
      title: 'Country of Incorporation (Optional)',
      icon: 'flag',
      isContent: false,
      content: getProfileListData(data?.countryOfIncorporation),
    },
    {
      title: 'State of Incorporation (Optional)',
      icon: 'map',
      isContent: false,
      content: getProfileListData(data?.stateOfIncorporation),
    },
    {
      title: 'Industry (Optional)',
      icon: 'factory',
      isContent: false,
      content: getProfileListData(data?.industrySector),
    },
    {
      title: 'CUSIP Number (Optional)',
      icon: 'pin',
      isContent: false,
      content: getProfileListData(data?.cusip),
    },
    {
      title: 'SIC Code (Optional)',
      icon: 'pin',
      isContent: false,
      content: getProfileListData(data?.sicCode),
    },
    {
      title: 'Domain Names',
      icon: 'dns',
      isContent: false,
      content: getProfileListData(data?.domainNames?.join(', ')),
    },
    {
      title: 'Description (Optional)',
      icon: 'description',
      isContent: false,
      content: getProfileListData(data?.businessDescription),
    },
    {
      title: 'Website',
      icon: 'language',
      isContent: false,
      content: getProfileListData(data?.websiteUrl),
    },
  ];
  const DTC_DETAILS = [
    {
      title: 'DTC Participant ID',
      icon: 'pin',
      isContent: false,
      content: (
        <div className='flex justify-between items-center gap-2 w-full'>
          {editStatus ? (
            <FormWrapper className='mb-0'>
              {/* <Label>Fmv</Label> */}
              <input
                className=''
                placeholder='DTC Participant ID'
                id='dtcParticipantId'
                name='dtcParticipantId'
                type='text'
                onChange={(e: any) => {
                  setDtcParticipantId(e.target.value);
                }}
                value={dtcParticipantId}
                style={{ width: '150px' }}
              />
            </FormWrapper>
          ) : (
            <p className='mb-4'>
              {dtcParticipantId ? dtcParticipantId : getProfileListData(dtcParticipantId)}
            </p>
          )}
          {editStatus ? (
            <div className='flex gap-2'>
              <Button
                title={'Cancel'}
                variant='secondary-red-outline'
                onClick={() => {
                  setDtcParticipantId(data?.dtcParticipantId);
                  setEditStatus(false);
                }}
              />
              <Button
                title={'Save'}
                variant='secondary-outline'
                onClick={() => {
                  handleSave();
                }}
                loading={loading}
              />
            </div>
          ) : (
            <Button
              title={'Edit'}
              variant='secondary-outline'
              onClick={() => {
                setEditStatus(true);
              }}
            />
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <ProfileContentWrapper id='companyProfileContentWrapper'>
      <div className='profile-header' id='companyProfileHeader'>
        <Heading title='Company Information' variant='h5' id='companyProfileTitle' />
        <Button
          as='Edit'
          variant='secondary-dark'
          title='Edit'
          icon='edit'
          id='companyProfileEditButton'
          onClick={() => {
            openModal();
          }}
        />
      </div>
      <ProfileContainer id='companyProfileContainer'>
        {loading && (
          <div id='companyProfileLoadingContainer'>
            {COMPANY_DETAILS.map((item, index) => (
              <div
                className='list list-loading'
                key={index}
                id={`companyProfileLoadingItem.${index}`}>
                <div className='list-icon' id={`companyProfileLoadingIcon.${index}`}>
                  <SkeletonCircle
                    size='table'
                    shape='circle'
                    id={`companyProfileLoadingSkeletonCircle.${index}`}
                  />
                </div>
                <div
                  className='list-content grid grid-cols-12'
                  id={`companyProfileLoadingContent.${index}`}>
                  <SkeletonLine
                    size='table'
                    containerClassName='col-span-3'
                    id={`companyProfileLoadingSkeletonLine1.${index}`}
                  />
                  <SkeletonLine
                    size='table'
                    containerClassName='col-span-9'
                    id={`companyProfileLoadingSkeletonLine2.${index}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && (
          <div id='companyProfileDataContainer'>
            {COMPANY_DETAILS.map((item, index) => (
              <div
                className='list grid sm:grid-cols-4 lg:grid-cols-12'
                key={index}
                id={`companyProfileItem.${index}`}>
                <div
                  className='list-icon sm:col-span-2 lg:col-span-4'
                  id={`companyProfileItemIcon.${index}`}>
                  <span
                    className='material-symbols-outlined'
                    id={`companyProfileItemIconSpan.${index}`}>
                    {item.icon}
                  </span>
                  <Heading title={item.title} variant='body-s' id={toCamelCase(item?.title)} />
                </div>
                <div
                  className='list-content sm:col-span-2 lg:col-span-8'
                  id={`companyProfileItemContent.${index}`}>
                  {item.isContent ? (
                    <div id={`companyProfileItemContentDiv.${index}`}>{item.content}</div>
                  ) : (
                    <Heading
                      title={item.content}
                      variant='body-s'
                      id={`companyProfileItemContentValue.${index}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ProfileContainer>

      <AnimationWrapper id='companyProfileAnimationWrapper'>
        {isModalOpen && (
          <CompanyModal
            initialData={data}
            loading={loading}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            id='companyProfileModal'
          />
        )}
      </AnimationWrapper>
      <div className='profile-header'>
        <Heading title='DTC Information' variant='h5' />
      </div>
      <ProfileContainer>
        {loading && (
          <>
            {DTC_DETAILS.map((item) => (
              <div className='list list-loading'>
                <div className='list-icon'>
                  <SkeletonCircle size='table' shape='circle' />
                </div>
                <div className='list-content grid grid-cols-12'>
                  <SkeletonLine size='table' containerClassName='col-span-3' />
                  <SkeletonLine size='table' containerClassName='col-span-9' />
                </div>
              </div>
            ))}
          </>
        )}
        {!loading &&
          DTC_DETAILS.map((item) => (
            <div className='list grid sm:grid-cols-4 lg:grid-cols-12'>
              <div className='list-icon sm:col-span-2 lg:col-span-4'>
                <span className='material-symbols-outlined'>{item.icon}</span>
                <Heading title={item.title} variant='body-s' />
              </div>
              <div className='list-content sm:col-span-2 lg:col-span-8'>
                {item.isContent ? (
                  <>
                    {item.content}
                  </>
                ) : (
                  <Heading title={item.content} id={toCamelCase(item?.title)} variant='body-s' />
                )}
              </div>
            </div>
          ))}
      </ProfileContainer>
    </ProfileContentWrapper>
  );
};

export default CompanyComponent;
